import { Injectable, Inject } from '@angular/core';
import { Action } from '@ngrx/store';
import { HttpStatusCode } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { CustomerStoreActions } from '../actions';
import { CustomerService } from '../services';
import { ApiError } from '@modules/checkout-store';
import { isApiError } from '@nx-ng/util';
import { LoggingService } from '@nx-ng/types';
import { LOGGER } from '@modules/core';

@Injectable()
export class VerificationEffects {
  constructor(
    private readonly actions$: Actions,
    private customerService: CustomerService,
    @Inject(LOGGER) private logger: LoggingService
  ) {}

  verifyMobileAppForCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerStoreActions.customerAPI_verifyMobileApp),
      mergeMap(
        (action: { emailAddress: string; basketItemId: number; quantityIndex: number } & Action) => {
          const { emailAddress, basketItemId, quantityIndex } = action;
          return this.customerService.verifyMobileApp(emailAddress).pipe(
            map((value, _index: number) => {
              if (value) {
                return CustomerStoreActions.customerAPI_verifyMobileAppSuccess({
                  verified: value.status === 'available',
                  basketItemId,
                  quantityIndex
                });
              }
              return CustomerStoreActions.customerAPI_verifyMobileAppFailure({
                error: 'Empty response received from mTicket verify',
                basketItemId,
                quantityIndex,
                retryAction: action,
              });
            }),
            catchError((err) => {
              const apiError: ApiError | string =
                err?.response?.parsedBody || err;

              if (isApiError(err) && err.code === HttpStatusCode.NotFound) {
                return of(
                  CustomerStoreActions.customerAPI_verifyMobileAppSuccess({
                    verified: false,
                    basketItemId,
                    quantityIndex
                  })
                );
              }

              this.logger.logApiError(
                {
                  apiError,
                },
                err
              );

              return of(
                CustomerStoreActions.customerAPI_verifyMobileAppFailure({
                  error: apiError,
                  basketItemId,
                  quantityIndex,
                  retryAction: action,
                })
              );
            })
          );
        }
      )
    )
  );

  verifySwiftCardForCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerStoreActions.customerAPI_verifySwiftCard),
      mergeMap(
        (action: { cardNumber: string; basketItemId: number; quantityIndex: number } & Action) => {
          const { cardNumber, basketItemId, quantityIndex } = action;
          return this.customerService.verifySwiftCard(cardNumber).pipe(
            map((value, _index: number) => {
              if (value) {
                return CustomerStoreActions.customerAPI_verifySwiftCardSuccess({
                  verified: value.status === 'verified',
                  basketItemId,
                  quantityIndex
                });
              }
              return CustomerStoreActions.customerAPI_verifySwiftCardFailure({
                error: 'empty response',
                basketItemId,
                quantityIndex,
                retryAction: action,
              });
            }),
            catchError((err) => {
              const apiError: ApiError | string =
                err?.response?.parsedBody || err;

              if (isApiError(err) && err.code === HttpStatusCode.NotFound) {
                return of(
                  CustomerStoreActions.customerAPI_verifySwiftCardSuccess({
                    verified: false,
                    basketItemId,
                    quantityIndex
                  })
                );
              }

              this.logger.logApiError(
                {
                  apiError,
                },
                err
              );

              return of(
                CustomerStoreActions.customerAPI_verifySwiftCardFailure({
                  error: apiError,
                  basketItemId,
                  quantityIndex,
                  retryAction: action,
                })
              );
            })
          );
        }
      )
    )
  );
}
